import React from 'react';
import millify from 'millify';
import { gql } from '@soundxyz/gql-string';
import { HeroInfo } from '@components/basics/Hero/HeroInfo';
import { ReleaseMintsModal } from '@components/modals/ReleaseMintsModal';
import { useModal } from '@context/modal';
import type { FragmentType } from '@graphql/reactQuery';
import {
  DraftHeroDetailsFragmentDoc,
  getFragment,
  SongHeroDetailsFragmentDoc,
} from '@graphql/reactQuery';
import { useSubscribeRelease } from '@hooks/useSubscribeRelease';
import { formatPlayDuration } from '@utils/helpers';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment SongHeroDetails on Release {
    id
    artist {
      id
      user {
        ...MinimalUser
      }
    }
    track {
      id
      duration
    }
    totalMinted
    ...ReleaseUpdates
  }

  fragment DraftHeroDetails on Draft {
    id
    artist {
      id
      user {
        ...MinimalUser
      }
    }
    info {
      releaseInfo {
        tracks {
          duration
        }
      }
    }
  }
`);

export const SongHeroDetails = ({
  releaseFrag,
}: {
  releaseFrag: FragmentType<SongHeroDetailsFragmentDoc>;
}) => {
  const release = getFragment(SongHeroDetailsFragmentDoc, releaseFrag);
  const {
    data: { totalMinted },
  } = useSubscribeRelease({ release });

  const { artist, track, id } = release;
  const { openModal } = useModal();

  const onMintsClick = () => {
    openModal('RELEASE_MINTS', {
      body: <ReleaseMintsModal releaseId={id} />,
    });
  };

  return (
    <HeroInfo
      userFrag={artist.user}
      primaryInfo={
        totalMinted ? (
          <button
            className="transition-[filter] duration-300 hover:brightness-130"
            onClick={onMintsClick}
          >
            {millify(totalMinted)} Mint{totalMinted === 1 ? '' : 's'}
          </button>
        ) : null
      }
      secondaryInfo={formatPlayDuration({ playTimeInSeconds: track.duration })}
    />
  );
};

export const DraftHeroDetails = ({
  draftFrag,
}: {
  draftFrag: FragmentType<DraftHeroDetailsFragmentDoc>;
}) => {
  const draft = getFragment(DraftHeroDetailsFragmentDoc, draftFrag);
  const { artist, info } = draft;
  const track = info?.releaseInfo?.tracks[0];

  return (
    <HeroInfo
      userFrag={artist.user}
      primaryInfo={null}
      secondaryInfo={formatPlayDuration({ playTimeInSeconds: track?.duration ?? 0 })}
    />
  );
};
